<template>
    <div v-if="user && wallets && wallets.length > 0">
        <menu-headless as="div" class="relative inline-block text-left">
            <div>
                <menu-button
                    class="inline-flex items-center gap-2 rounded-lg border border-gray-600 bg-gray-700/50 px-3 py-2 text-sm transition-colors hover:border-gray-500 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                >
                    <font-awesome-icon
                        icon="fa-solid fa-wallet"
                        :class="[
                            'text-sm',
                            selectedWallet
                                ? 'text-indigo-400'
                                : 'text-gray-400',
                        ]"
                    />
                    <span
                        v-if="selectedWallet"
                        class="font-medium text-slate-200"
                    >
                        {{ formatWalletLabel(selectedWallet) }}
                    </span>
                    <span v-else class="text-gray-400"
                        >Selecione uma carteira</span
                    >
                    <font-awesome-icon
                        icon="fa-solid fa-chevron-down"
                        class="ml-1 h-3 w-3 text-gray-400"
                    />
                </menu-button>
            </div>

            <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
            >
                <menu-items
                    class="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-700 rounded-md bg-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                    <div v-if="loading" class="py-4 text-center text-slate-50">
                        <font-awesome-icon
                            icon="fa-solid fa-spinner"
                            class="animate-spin"
                        />
                        <span class="ml-2">Carregando...</span>
                    </div>

                    <div
                        v-else-if="wallets.length > 0"
                        class="divide-y divide-gray-100 py-1 shadow dark:divide-gray-600 dark:bg-gray-700"
                    >
                        <menu-item
                            v-for="wallet in wallets"
                            :key="wallet.id"
                            v-slot="{ active }"
                            @click="handleWalletSelect(wallet)"
                            class="hover:cursor-pointer"
                        >
                            <p
                                :class="[
                                    active
                                        ? 'bg-gray-800/25 text-slate-200'
                                        : 'text-slate-50',
                                    selectedWallet?.id === wallet.id
                                        ? 'bg-indigo-500/20 font-semibold'
                                        : '',
                                    'block px-4 py-2 text-sm',
                                ]"
                            >
                                <font-awesome-icon
                                    icon="fa-solid fa-wallet"
                                    class="mr-2"
                                    aria-hidden="true"
                                />
                                {{ formatWalletLabel(wallet) }}
                            </p>
                        </menu-item>
                    </div>

                    <div v-else class="py-4 text-center text-sm text-slate-400">
                        Nenhuma carteira disponível
                    </div>
                </menu-items>
            </transition>
        </menu-headless>
    </div>
</template>

<script>
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { mapWritableState } from "pinia";
import { mapActions } from "pinia";
import { Store } from "../../stores/store";
import ApproveUserModal from "./modals/ApproveUserModal.vue";
import BlockUserModal from "./modals/BlockUserModal.vue";
import ImpersonateUserModal from "./modals/ImpersonateUserModal.vue";
import UpdateEmailModal from "./modals/UpdateEmailModal.vue";
import PermissionMixin from "../../mixins/PermissionMixin.vue";
import Modal from "../../components/layouts/Modal.vue";

export default {
    components: {
        UpdateEmailModal,
        MenuHeadless: Menu,
        MenuButton,
        MenuItem,
        MenuItems,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        ApproveUserModal,
        BlockUserModal,
        ImpersonateUserModal,
        Modal,
    },
    props: {
        selectedWallet: {
            type: Object,
            default: null,
        },
        onWalletChange: {
            type: Function,
            required: true,
        },
        onReady: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            wallets: [],
        };
    },
    computed: {
        ...mapWritableState(Store, ["user", "permissions"]),
    },
    mounted() {
        this.fetchWallets();
    },
    methods: {
        ...mapActions(Store, ["fetchUser"]),
        fetchWallets() {
            if (!this.user?.id) {
                this.onReady?.();

                return;
            }

            this.loading = true;

            axios
                .get(`/api/user/${this.user.id}/wallet`)
                .then(({ data }) => {
                    this.wallets = data;
                    this.loading = false;

                    // Seleciona a primeira wallet automaticamente se nenhuma estiver selecionada
                    if (data.length > 0 && !this.selectedWallet) {
                        this.handleWalletSelect(data[0]);
                    }
                })
                .catch((error) => {
                    console.error("Erro ao buscar carteiras:", error);
                    this.loading = false;
                    this.toast?.error(
                        error.response?.data?.message ||
                            "Erro ao carregar carteiras",
                    );
                })
                .finally(() => {
                    this.onReady?.();
                });
        },
        handleWalletSelect(wallet) {
            this.onWalletChange(wallet);
        },
        formatWalletLabel(wallet) {
            if (!wallet) {
                return "";
            }

            const origin = wallet.origin || "N/A";
            const currency = wallet.currency?.iso_code?.toUpperCase() || "BRL";

            return `${origin.charAt(0).toUpperCase() + origin.slice(1)} (${currency})`;
        },
    },
};
</script>

<style></style>
